import React, { useState, forwardRef, useRef, useLayoutEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import StatusBadge from "./expert-applications/StatusBadge";
import ExpertPublishingStatus from "./expert-applications/ExpertPublishingStatus";
import TableRowActions from "./expert-applications/TableRowActions";
import { FixedSizeList as List } from 'react-window';

interface ExpertApplication {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  professional_title: string;
  years_experience: number;
  specializations: string[];
  bio: string;
  hourly_rate: number;
  linkedin_url: string | null;
  website_url: string | null;
  motivation_letter: string | null;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ExpertApplicationsTableProps {
  applications: ExpertApplication[];
  onSelectApplication: (application: ExpertApplication) => void;
  selectedApplicationId?: string;
}

// Custom TableBody wrapper for react-window
const VirtualizedTableBody = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  (props, ref) => <tbody ref={ref} {...props} />
);

export default function ExpertApplicationsTable({ 
  applications, 
  onSelectApplication, 
  selectedApplicationId 
}: ExpertApplicationsTableProps) {
  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(applications.length / pageSize);
  const paginatedApps = applications.slice((page - 1) * pageSize, page * pageSize);

  // Responsive width for virtualization
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    function updateWidth() {
      if (tableContainerRef.current) {
        setContainerWidth(tableContainerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // console.log("🎨 Rendering ExpertApplicationsTable with", applications.length, "applications");
  
  return (
    <div className="w-full min-w-[600px]" ref={tableContainerRef}>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Experience</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead>Applied</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Publishing</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        {paginatedApps.length > 20 && containerWidth > 0 ? (
          <List
            height={Math.min(pageSize, paginatedApps.length) * 56}
            itemCount={paginatedApps.length}
            itemSize={56}
            width={containerWidth}
            outerElementType={VirtualizedTableBody}
          >
            {({ index, style }) => {
              const application = paginatedApps[index];
              return (
                <TableRow 
                  key={application.id}
                  className={selectedApplicationId === application.id ? "bg-muted/50" : ""}
                  style={{ ...style, display: 'table-row' }}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">{application.full_name}</div>
                      <div className="text-sm text-muted-foreground">{application.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px] truncate" title={application.professional_title}>
                      {application.professional_title}
                    </div>
                  </TableCell>
                  <TableCell>{application.years_experience} years</TableCell>
                  <TableCell>€{application.hourly_rate}/hr</TableCell>
                  <TableCell>
                    {format(new Date(application.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={application.status} />
                  </TableCell>
                  <TableCell>
                    {application.status === 'approved' ? (
                      <ExpertPublishingStatus applicationId={application.id} />
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <TableRowActions 
                      application={application} 
                      onSelectApplication={onSelectApplication} 
                    />
                  </TableCell>
                </TableRow>
              );
            }}
          </List>
        ) : (
          <TableBody>
            {paginatedApps.map((application) => (
              <TableRow 
                key={application.id}
                className={selectedApplicationId === application.id ? "bg-muted/50" : ""}
              >
                <TableCell>
                  <div>
                    <div className="font-medium">{application.full_name}</div>
                    <div className="text-sm text-muted-foreground">{application.email}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="max-w-[200px] truncate" title={application.professional_title}>
                    {application.professional_title}
                  </div>
                </TableCell>
                <TableCell>{application.years_experience} years</TableCell>
                <TableCell>€{application.hourly_rate}/hr</TableCell>
                <TableCell>
                  {format(new Date(application.created_at), "MMM d, yyyy")}
                </TableCell>
                <TableCell>
                  <StatusBadge status={application.status} />
                </TableCell>
                <TableCell>
                  {application.status === 'approved' ? (
                    <ExpertPublishingStatus applicationId={application.id} />
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <TableRowActions 
                    application={application} 
                    onSelectApplication={onSelectApplication} 
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-2">
          <button
            className="px-2 py-1 border rounded disabled:opacity-50"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`px-2 py-1 border rounded ${page === i + 1 ? 'bg-muted font-bold' : ''}`}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="px-2 py-1 border rounded disabled:opacity-50"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
